import React, { useMemo } from "react";

import { mdiLoading, mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { useDropzone } from "react-dropzone";

import type { JSX } from "react";

import type { ImageRatio } from "./ModuleImageUploadWithTranslation";
import type { PictureKeys } from "../../constants/ratio";

export const UPLOAD_ZONE_IMAGE_HEIGHT = 160;

interface UploadZoneProps {
  onDrop: ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }) => void;
  imageRatio: ImageRatio;
  isDisabled?: boolean;
  imagePreview?: string | null;
  translationId: string;
  isLoading?: boolean;
}

export default function UploadZone({
  onDrop,
  imageRatio,
  isDisabled,
  imagePreview,
  translationId,
  isLoading,
}: UploadZoneProps): JSX.Element {
  const handleDrop = (fileList: File[]): void => {
    if (fileList[0]) {
      onDrop({
        file: fileList[0],
        pictureFormat: imageRatio.id,
        translationId,
      });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [],
    },
    onDrop: handleDrop,
  });

  const rootProps = isDisabled ? {} : getRootProps();

  const rootClassName = classNames(
    "bg-surface-muted text-foreground/30 bg-center bg-cover",
    isDisabled ? "auto" : "cursor-pointer hover:text-primary",
  );

  const dynamicRootStyles = useMemo(
    () =>
      !imagePreview
        ? {}
        : {
            backgroundImage: `url(${imagePreview})`,
          },
    [imagePreview],
  );

  return (
    <div
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rootProps}
      className={rootClassName}
      style={{
        ...dynamicRootStyles,
        aspectRatio: imageRatio.ratio,
        height: `${UPLOAD_ZONE_IMAGE_HEIGHT}px`,
      }}
    >
      <div className="flex size-full flex-col items-center justify-center gap-[0.46875rem]">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} disabled={isDisabled} />
        {!isDisabled && !isLoading && <Icon path={mdiUpload} size={2.5} />}
        {isLoading ? (
          <div className="text-primary">
            <Icon spin path={mdiLoading} size={2.5} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
